// Iter-201 — Role Permissions matrix: lets the CEO/ADMIN flip individual
// permissions on/off per system role (with CEO locked as supreme). Persists
// overrides on top of the hardcoded defaults so the page is reflective —
// each cell shows whether it's a default-on, default-off, granted-extra,
// or revoked permission.
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Lock, ChevronDown, ChevronRight, Info, RotateCcw, Shield, Sparkles, Users as UsersIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ROLE_LABELS_FR = {
  CEO: "CEO",
  ADMIN: "Admin",
  GESTIONNAIRE: "Gestionnaire",
  MANAGER: "Manager",
  MANAGER_FREELANCE: "Manager Freelance",
  COLLABORATOR_INTERNAL: "Collaborateur Interne",
  COLLABORATOR_FREELANCE: "Collaborateur Freelance",
};

const ROLE_COLOR = {
  CEO: "bg-violet-100 text-violet-800 border-violet-300",
  ADMIN: "bg-blue-100 text-blue-800 border-blue-300",
  GESTIONNAIRE: "bg-emerald-100 text-emerald-800 border-emerald-300",
  MANAGER: "bg-amber-100 text-amber-800 border-amber-300",
  MANAGER_FREELANCE: "bg-orange-100 text-orange-800 border-orange-300",
  COLLABORATOR_INTERNAL: "bg-zinc-100 text-zinc-800 border-zinc-300",
  COLLABORATOR_FREELANCE: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function RolePermissions() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [matrix, setMatrix] = useState({});   // role_key -> Set<perm>
  const [original, setOriginal] = useState({});
  const [userCounts, setUserCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [selectedRole, setSelectedRole] = useState("ADMIN");

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/permissions/catalog"),
      api.get("/admin/role-permissions"),
    ]).then(([cat, mtx]) => {
      if (!alive) return;
      setCatalog(cat.data);
      const m = {};
      const orig = {};
      const counts = {};
      mtx.data.forEach((row) => {
        m[row.role_key] = new Set(row.effective);
        orig[row.role_key] = new Set(row.effective);
        counts[row.role_key] = row.active_user_count || 0;
      });
      setMatrix(m);
      setOriginal(orig);
      setUserCounts(counts);
      // Initial expansion: open the first module.
      const firstMod = Object.keys(cat.data.catalog || {})[0];
      if (firstMod) setExpanded({ [firstMod]: true });
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Échec chargement permissions");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const togglePermission = (roleKey, permKey) => {
    if (roleKey === "CEO") return;  // locked supreme role
    setMatrix((prev) => {
      const next = { ...prev };
      const set = new Set(next[roleKey] || []);
      if (set.has(permKey)) set.delete(permKey); else set.add(permKey);
      next[roleKey] = set;
      return next;
    });
  };

  const diff = useMemo(() => {
    const out = {};
    Object.keys(matrix).forEach((rk) => {
      const cur = matrix[rk];
      const orig = original[rk] || new Set();
      const added = [...cur].filter((p) => !orig.has(p));
      const removed = [...orig].filter((p) => !cur.has(p));
      if (added.length || removed.length) out[rk] = { added, removed };
    });
    return out;
  }, [matrix, original]);

  const dirty = Object.keys(diff).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      for (const rk of Object.keys(diff)) {
        await api.put(`/admin/role-permissions/${rk}`, {
          permissions: [...(matrix[rk] || [])],
        });
      }
      // Re-fetch to refresh `original` + user counts.
      const r = await api.get("/admin/role-permissions");
      const m = {}, orig = {}, counts = {};
      r.data.forEach((row) => {
        m[row.role_key] = new Set(row.effective);
        orig[row.role_key] = new Set(row.effective);
        counts[row.role_key] = row.active_user_count || 0;
      });
      setMatrix(m); setOriginal(orig); setUserCounts(counts);
      toast.success(`Permissions mises à jour pour ${Object.keys(diff).length} rôle(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec enregistrement");
    } finally { setSaving(false); }
  };

  const resetRoleToDefault = (roleKey) => {
    if (!catalog || roleKey === "CEO") return;
    const def = new Set(catalog.role_defaults?.[roleKey] || []);
    setMatrix((prev) => ({ ...prev, [roleKey]: def }));
  };

  if (loading || !catalog) {
    return <div className="p-8 text-center text-sm text-zinc-500"><Loader2 size={16} className="animate-spin inline mr-2" />Chargement…</div>;
  }

  const modules = Object.entries(catalog.catalog || {});
  const roleKeys = catalog.role_keys || [];

  return (
    <div className="space-y-6 p-6 max-w-6xl" data-testid="role-permissions-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500">People · Sécurité</div>
        <h1 className="font-heading text-2xl text-zinc-900 mt-1">Permissions par rôle officiel</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-3xl">
          Activez ou désactivez chaque fonctionnalité pour les 7 rôles système BAIM. Les modifications s'appliquent <strong>en plus</strong> des permissions par défaut (granted) ou <strong>en retirent</strong> certaines (revoked). Le rôle <strong>CEO</strong> est suprême : ses permissions ne peuvent pas être modifiées.
        </p>
      </div>

      {/* Role selector */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="role-selector">
        <div className="text-[11px] uppercase tracking-widest font-mono text-zinc-500">Sélectionner un rôle</div>
        <div className="flex flex-wrap gap-2">
          {roleKeys.map((rk) => {
            const active = rk === selectedRole;
            const d = diff[rk];
            const isLocked = rk === "CEO";
            const nUsers = userCounts[rk] || 0;
            return (
              <button
                key={rk}
                type="button"
                onClick={() => setSelectedRole(rk)}
                data-testid={`role-tab-${rk}`}
                title={`${nUsers} utilisateur(s) actif(s) avec ce rôle`}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-xs font-medium transition-all ${active ? "bg-zinc-950 text-white border-zinc-950 shadow-sm" : `${ROLE_COLOR[rk]} hover:opacity-80`}`}
              >
                {isLocked && <Lock size={11} />}
                {ROLE_LABELS_FR[rk]}
                <span className={`ml-1 text-[10px] font-mono ${active ? "opacity-75" : "opacity-60"}`}>
                  · {(matrix[rk] || new Set()).size}p
                </span>
                <span
                  data-testid={`role-tab-count-${rk}`}
                  className={`ml-0.5 inline-flex items-center gap-1 text-[10px] font-mono rounded-full px-1.5 py-0.5 ${active ? "bg-white/20 text-white" : "bg-white/70 text-zinc-700 border border-zinc-300"}`}
                >
                  <UsersIcon size={9} /> {nUsers}
                </span>
                {d && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-400" title="Modifié — non enregistré" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Current selection: header */}
      <div className={`border rounded-lg p-4 flex items-center gap-3 ${selectedRole === "CEO" ? "bg-violet-50 border-violet-200" : "bg-zinc-50 border-zinc-200"}`}>
        {selectedRole === "CEO" ? <Shield size={18} className="text-violet-700" /> : <Sparkles size={18} className="text-zinc-700" />}
        <div className="flex-1">
          <div className="font-heading text-base text-zinc-900">{ROLE_LABELS_FR[selectedRole]}</div>
          <div className="text-xs text-zinc-600 mt-0.5 flex items-center gap-3 flex-wrap">
            <span>
              <strong>{(matrix[selectedRole] || new Set()).size}</strong> permission(s) actives sur {catalog.all_permissions.length}.
            </span>
            <span className="inline-flex items-center gap-1 text-zinc-700 font-medium" data-testid={`header-user-count-${selectedRole}`}>
              <UsersIcon size={12} className="text-zinc-500" />
              <strong>{userCounts[selectedRole] || 0}</strong> utilisateur(s) actif(s) avec ce rôle
            </span>
            {selectedRole === "CEO" && <span className="font-semibold text-violet-700">· Suprême (non modifiable)</span>}
            {diff[selectedRole] && (
              <span className="text-amber-700 font-semibold">
                · {diff[selectedRole].added.length} ajout(s), {diff[selectedRole].removed.length} retrait(s) à enregistrer
              </span>
            )}
          </div>
        </div>
        {selectedRole !== "CEO" && (
          <button
            type="button"
            onClick={() => resetRoleToDefault(selectedRole)}
            data-testid="reset-role-default-btn"
            className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-zinc-300 bg-white rounded hover:bg-zinc-50"
          >
            <RotateCcw size={12} /> Réinitialiser aux valeurs par défaut
          </button>
        )}
      </div>

      {/* Modules (collapsible sections) */}
      <div className="space-y-2">
        {modules.map(([modName, perms]) => {
          const isOpen = !!expanded[modName];
          const grantedCount = perms.filter((p) => (matrix[selectedRole] || new Set()).has(p.key)).length;
          return (
            <div key={modName} className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [modName]: !e[modName] }))}
                data-testid={`module-toggle-${modName}`}
                className="w-full flex items-center gap-2 px-4 py-3 hover:bg-zinc-50 text-left"
              >
                {isOpen ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronRight size={14} className="text-zinc-500" />}
                <div className="flex-1">
                  <div className="text-sm font-semibold text-zinc-900">{modName}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{grantedCount}/{perms.length} permission(s) actives pour {ROLE_LABELS_FR[selectedRole]}</div>
                </div>
                <div className="text-[10px] font-mono text-zinc-400">{perms.length} perms</div>
              </button>
              {isOpen && (
                <div className="border-t border-zinc-100 divide-y divide-zinc-100">
                  {perms.map((p) => {
                    const checked = (matrix[selectedRole] || new Set()).has(p.key);
                    const isDefault = new Set(catalog.role_defaults?.[selectedRole] || []).has(p.key);
                    const isLocked = selectedRole === "CEO";
                    return (
                      <label
                        key={p.key}
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 ${isLocked ? "cursor-not-allowed opacity-75" : "cursor-pointer"}`}
                        data-testid={`perm-row-${p.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isLocked}
                          onChange={() => togglePermission(selectedRole, p.key)}
                          data-testid={`perm-checkbox-${selectedRole}-${p.key}`}
                          className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-zinc-950 focus:ring-zinc-950"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-medium text-zinc-900">{p.label}</div>
                            {isDefault && checked && <span className="text-[9px] uppercase tracking-wider font-mono text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Défaut</span>}
                            {!isDefault && checked && <span className="text-[9px] uppercase tracking-wider font-mono text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Ajouté</span>}
                            {isDefault && !checked && <span className="text-[9px] uppercase tracking-wider font-mono text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">Retiré</span>}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5 leading-snug">{p.desc}</div>
                          <div className="text-[10px] text-zinc-400 font-mono mt-1">{p.key}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save bar */}
      <div className="sticky bottom-2 flex justify-between items-center bg-white border border-zinc-200 rounded-lg p-3 shadow-sm">
        <div className="text-xs text-zinc-600 flex items-center gap-2 flex-wrap">
          <Info size={12} className="text-zinc-400" />
          {dirty ? (
            <>
              <span><strong>{Object.keys(diff).length}</strong> rôle(s) avec modifications non enregistrées.</span>
              <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid="save-bar-impact">
                <UsersIcon size={11} />
                Impact : <strong>{Object.keys(diff).reduce((s, rk) => s + (userCounts[rk] || 0), 0)}</strong> utilisateur(s) concerné(s)
              </span>
            </>
          ) : (
            <span>Aucune modification en attente.</span>
          )}
        </div>
        <button
          onClick={save}
          disabled={saving || !dirty}
          data-testid="role-permissions-save-btn"
          className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}
